import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';

import ConcurrentReviewSlideout, { TabTypeValue } from 'components/patient/concurrentReview/ConcurrentReviewSlideout';
import { useAuthorization } from 'context/review/authorization';
import { useAuthorizationId } from 'hooks/useAuthorizationId';
import Authorization from 'models/reviews/Authorization';

type RoutedConcurrentReviewSlideoutProps = {
  onClose?: (authorization?: Authorization) => void;
  onUpdateAuthorization?: () => void;
};

export default function RoutedConcurrentReviewSlideout({
  onClose,
  onUpdateAuthorization,
}: RoutedConcurrentReviewSlideoutProps) {
  const { authorization, setAuthorizationId } = useAuthorization();
  const navigate = useNavigate();
  const location = useLocation();
  const authorizationId = useAuthorizationId();
  const [isOpen, setIsOpen] = useState(false);

  const isOnConcurrentReviewsTab = useMatch('/patients/:episodeId/concurrent-reviews/*');
  const isOnPatientStoryTab = useMatch('/patients/:episodeId/authorization/*');

  const basePath = useMemo(() => {
    if (!authorizationId) return '';

    if (isOnConcurrentReviewsTab) {
      return `/patients/${isOnConcurrentReviewsTab.params.episodeId}/concurrent-reviews/${authorizationId}`;
    } else if (isOnPatientStoryTab) {
      return `/patients/${isOnPatientStoryTab.params.episodeId}/authorization/${authorizationId}`;
    }
    return '';
  }, [authorizationId, isOnConcurrentReviewsTab, isOnPatientStoryTab]);

  const tabFromPath = useMemo((): TabTypeValue | undefined => {
    const pathname = location.pathname.replace(/\/+$/, '');
    if (pathname.endsWith('/summary')) return 'summary';
    if (pathname.endsWith('/files')) return 'files';
    if (pathname.endsWith('/comparison')) return 'comparison';
    return undefined;
  }, [location.pathname]);

  useEffect(() => {
    if (authorizationId) setAuthorizationId(authorizationId);
  }, [authorizationId, setAuthorizationId]);

  useLayoutEffect(() => {
    const isSlideoutOpen = !!authorizationId && !!authorization;
    setIsOpen(isSlideoutOpen);

    if (isSlideoutOpen) {
      // Redirect to /files if no valid tab
      if (!tabFromPath) {
        navigate('files', { replace: true, relative: 'path' });
      }
    }
  }, [authorizationId, authorization, tabFromPath, navigate]);

  const handleClose = (authorization?: Authorization) => {
    onClose?.(authorization);

    if (isOnConcurrentReviewsTab) {
      // From concurrent reviews tab: /concurrent-reviews/:authId/:tab → /concurrent-reviews
      navigate(`/patients/${isOnConcurrentReviewsTab.params.episodeId}/concurrent-reviews`);
    } else if (isOnPatientStoryTab) {
      // From patient story tab: /authorization/:authId/:tab → / (patient story)
      navigate(`/patients/${isOnPatientStoryTab.params.episodeId}`);
    }
  };

  const handleTabChange = (tabValue: TabTypeValue) => {
    if (basePath) {
      navigate(`${basePath}/${tabValue}`);
    }
  };

  return (
    <ConcurrentReviewSlideout
      open={isOpen}
      onClose={handleClose}
      onUpdateAuthorization={onUpdateAuthorization}
      initialTabValue={tabFromPath}
      onTabChange={handleTabChange}
    />
  );
}
